export type SlideDesign = 'slideContent' | 'slideFullContent' | 'splitContent';

export type ContentBrand = 'gt' | 'demo';

export interface ContentLeaf {
  kind: 'leaf';
  id: string;
  label: string;
  compatibleWith: SlideDesign[];
  needsContentArea: boolean;
  requiresBrand?: ContentBrand;
}

export interface ContentGroup {
  kind: 'group';
  id: string;
  label: string;
  compatibleWith: SlideDesign[];
  requiresBrand?: ContentBrand;
  promptLabel?: string;
  children: ContentLeaf[];
}

export type ContentTypeEntry = ContentLeaf | ContentGroup;

export const contentTypes: ContentTypeEntry[] = [
  {
    kind: 'group',
    id: 'ownTextGroup',
    label: 'Egna texter',
    compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
    promptLabel: 'Vilken typ av egen text vill du skapa?',
    children: [
      {
        kind: 'leaf',
        id: 'contentList',
        label: 'Egna punkter',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
      {
        kind: 'leaf',
        id: 'contentText',
        label: 'Egen brödtext',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'diagramGroup',
    label: 'Diagram',
    compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
    promptLabel: 'Vilken typ av diagram vill du skapa?',
    children: [
      {
        kind: 'leaf',
        id: 'contentChart',
        label: 'Färdiga diagram',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
      {
        kind: 'leaf',
        id: 'customDiagram',
        label: 'Tomma diagram',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
    ],
  },
  {
    kind: 'leaf',
    id: 'contentTable',
    label: 'Färdiga tabeller',
    compatibleWith: ['slideContent', 'slideFullContent'],
    needsContentArea: true,
  },
  {
    kind: 'leaf',
    id: 'contentMore',
    label: 'GT-bibliotek',
    compatibleWith: ['slideContent', 'slideFullContent'],
    needsContentArea: true,
    requiresBrand: 'gt',
  },
  {
    kind: 'leaf',
    id: 'contentMore',
    label: 'Innehållsbibliotek',
    compatibleWith: ['slideContent', 'slideFullContent'],
    needsContentArea: true,
    requiresBrand: 'demo',
  },
  {
    kind: 'group',
    id: 'mediaGroup',
    label: 'Bifoga',
    compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
    promptLabel: 'Vad vill du bifoga?',
    children: [
      {
        kind: 'leaf',
        id: 'contentImage',
        label: 'Importera bild',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
      {
        kind: 'leaf',
        id: 'contentPdf',
        label: 'Bädda in PDF',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'toolsGroup',
    label: 'Övrigt',
    compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
    promptLabel: 'Vad vill du lägga till?',
    children: [
      {
        kind: 'leaf',
        id: 'contentAiInsights',
        label: 'AI-insikter',
        compatibleWith: ['slideFullContent'],
        needsContentArea: false,
      },
      {
        kind: 'leaf',
        id: 'contentSchedule',
        label: 'Tidsplan',
        compatibleWith: ['slideContent', 'slideFullContent'],
        needsContentArea: true,
      },
      {
        kind: 'leaf',
        id: 'contentYearWheel',
        label: 'Årshjul',
        compatibleWith: ['slideContent', 'slideFullContent'],
        needsContentArea: true,
      },
      {
        kind: 'leaf',
        id: 'orgChart',
        label: 'Koncernträd',
        compatibleWith: ['slideContent', 'slideFullContent', 'splitContent'],
        needsContentArea: true,
        requiresBrand: 'gt',
      },
      {
        kind: 'leaf',
        id: 'contentTeam',
        label: 'Team',
        compatibleWith: ['slideFullContent'],
        needsContentArea: false,
      },
      {
        kind: 'leaf',
        id: 'contentVisual',
        label: 'Bildblock',
        compatibleWith: ['slideFullContent'],
        needsContentArea: false,
      },
    ],
  },
];

function flattenLeaves(entries: ContentTypeEntry[]): ContentLeaf[] {
  const leaves: ContentLeaf[] = [];
  for (const entry of entries) {
    if (entry.kind === 'leaf') {
      leaves.push(entry);
    } else {
      for (const child of entry.children) {
        leaves.push(child);
      }
    }
  }
  return leaves;
}

const leavesById = new Map<string, ContentLeaf>();
for (const leaf of flattenLeaves(contentTypes)) {
  if (!leavesById.has(leaf.id)) {
    leavesById.set(leaf.id, leaf);
  }
}

export function findLeaf(contentTypeId: string): ContentLeaf | undefined {
  return leavesById.get(contentTypeId);
}

export function needsContentArea(contentTypeId: string): boolean {
  const leaf = findLeaf(contentTypeId);
  if (!leaf) {
    return true;
  }
  return leaf.needsContentArea;
}

export function isCompatible(
  entry: ContentTypeEntry,
  design: SlideDesign,
): boolean {
  return entry.compatibleWith.indexOf(design) >= 0;
}

export function sortEntriesByCompatibility(
  entries: ContentTypeEntry[],
  design: SlideDesign,
): ContentTypeEntry[] {
  return [...entries].sort((a, b) => {
    const aActive = isCompatible(a, design) ? 0 : 1;
    const bActive = isCompatible(b, design) ? 0 : 1;
    return aActive - bActive;
  });
}

export function matchesBrand(
  entry: ContentTypeEntry,
  brand: ContentBrand,
): boolean {
  if (!entry.requiresBrand) {
    return true;
  }
  return entry.requiresBrand === brand;
}

export function matchesAiInsightsEnabled(
  entry: Pick<ContentLeaf, 'id'>,
  aiInsightsEnabled: boolean,
): boolean {
  if (entry.id === 'contentAiInsights') {
    return aiInsightsEnabled;
  }
  return true;
}

export function normalizeSlide(slide: any): void {
  if (!slide) {
    return;
  }
  if (slide.slideType === 'splitContent') {
    if (!slide.subSlides || !Array.isArray(slide.subSlides)) {
      slide.subSlides = [];
    }
    while (slide.subSlides.length < 2) {
      slide.subSlides.push({
        id: slide.subSlides.length + 1,
        description: '',
        contentType: '',
      });
    }
    if (slide.subSlides.length > 2) {
      slide.subSlides = slide.subSlides.slice(0, 2);
    }
  }
  if (slide.slideType === 'slideFullContent') {
    if (!slide.subSlides || slide.subSlides.length === 0) {
      const migrated: any = {
        id: 1,
        description: '',
        contentType: slide.contentType || '',
      };
      if (slide.teamName !== undefined) {
        migrated.title = slide.teamName;
      }
      if (slide.teamMembers !== undefined) {
        migrated.teamMembers = slide.teamMembers;
      }
      slide.subSlides = [migrated];
      delete slide.contentType;
      delete slide.teamName;
      delete slide.teamMembers;
    } else if (
      slide.subSlides[0] &&
      !slide.subSlides[0].contentType &&
      slide.contentType
    ) {
      const first = slide.subSlides[0];
      first.contentType = slide.contentType;
      if (slide.teamName !== undefined && first.title === undefined) {
        first.title = slide.teamName;
      }
      if (slide.teamMembers !== undefined && first.teamMembers === undefined) {
        first.teamMembers = slide.teamMembers;
      }
      delete slide.contentType;
      delete slide.teamName;
      delete slide.teamMembers;
    }
  }

  if (slide.subSlides && slide.subSlides.length) {
    for (const sub of slide.subSlides) {
      if (!sub) {
        continue;
      }
      if (sub.contentType === 'contentDiagram' && !sub.chart) {
        sub.contentType = '';
      }
      if (sub.teamName !== undefined && sub.title === undefined) {
        sub.title = sub.teamName;
      }
      if (sub.teamName !== undefined) {
        delete sub.teamName;
      }
    }
  }
}

export function normalizeSlides(slides: any[]): void {
  if (!slides || !slides.length) {
    return;
  }
  for (const slide of slides) {
    normalizeSlide(slide);
  }
}
